import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import document from 'global/document';
import Typography from '@digital-hig/mui/dist/Typography';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
import parse, { domToReact } from 'html-react-parser';
// eslint-disable-next-line import/no-extraneous-dependencies
import Button from '@mui/material/Button';
// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import SvgCtaArrowRight from '@digital-hig/icon/lib/esm/icons/cta-arrow-right';
import SvgComplete from '@digital-hig/icon/lib/esm/display-icons/complete';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dateFormat from 'dateformat';
// eslint-disable-next-line import/no-extraneous-dependencies
import Divider from '@mui/material/Divider';
import {
  styleVariables, LRS_VERB_COMPLETED, ENVIRONMENT, OBJECT_TYPE_COURSE, COURSE_TYPE_OUTCOMES_CAPABILITIES, COURSE_TYPE_SKILL_BUILDER,
} from '../common/constants';
import { getUserDetails, getUserId } from '../store/userSlice';
import { getMostRecentStatementOfType } from '../common/userUtils';
import EnvContext from '../common/envContext';
import { triggerManualLinkClick } from '../common/analyticsApi';

const triggerLinkClick = ({ element }) => {
  triggerManualLinkClick({ linkTitle: element?.textContent });
};

const parseHTMLString = ({ html, userId }) => {
  let pIndex = 0;
  let aIndex = 0;

  const generateLink = (elem) => {
    const newHref = elem?.attribs?.href.replace('{oxygen}', userId);
    return (
      <Button onClick={(e) => { triggerLinkClick({ element: e.target }); }} href={newHref} className="cta" target="_blank" key="cta" startIcon={<SvgIcon fontSize="medium"><SvgCtaArrowRight /></SvgIcon>} variant="text">{domToReact(elem?.children)}</Button>
    );
  };

  const options = {
    // eslint-disable-next-line consistent-return
    replace: (domNode) => {
      const {
        attribs, children, type, name, parent,
      } = domNode;
      if (!attribs) {
        return <div id="removeMeParseHTMLString" />;
      }
      if (parent === null) {
        if (type === 'tag' && name === 'p' && pIndex < 3) {
          if (pIndex === 0) {
            pIndex = 1;
            // eslint-disable-next-line consistent-return
            return <Typography variant="headline-small" key="headline">{domToReact(children)}</Typography>;
          }
          if (pIndex === 1) {
            pIndex = 2;
            // eslint-disable-next-line consistent-return
            return <Typography variant="body-copy-medium" className="blockBody" key="body">{domToReact(children)}</Typography>;
          }
          if (pIndex === 2 && aIndex === 0) {
            pIndex = 3;
            if (children.length > 0 && children[0]?.name === 'a') {
              // Link is wrapped in a P tag
              aIndex = 1;
              // eslint-disable-next-line consistent-return
              return generateLink(children[0]);
            }
            // eslint-disable-next-line consistent-return
            return <div id="removeMeParseHTMLString" />;
          }
        }
        if (type === 'tag' && name === 'a' && aIndex === 0) {
          aIndex = 1;
          // eslint-disable-next-line consistent-return
          return generateLink(domNode);
        }
        // eslint-disable-next-line consistent-return
        return <div id="removeMeParseHTMLString" />;
      }
    },
  };
  return parse(html, options);
};

const NextSteps = ({ data, learningRecords }) => {
  const { first } = useSelector(getUserDetails);
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];

  const userId = useSelector(getUserId);

  const completeStatement = getMostRecentStatementOfType({
    learningRecords, id: data.id, env, verb: LRS_VERB_COMPLETED,
  });
  const completionDate = completeStatement?.timestamp ? dateFormat(completeStatement.timestamp, 'mmmm d, yyyy', true) : '';
  const dataType = data.objectType;
  const nextStepBlocks = [
    <Grid item sm={12} md={6} lg={6} key="related">
      <Typography variant="headline-small">Discover related learning</Typography>
      <Typography variant="body-copy-medium" className="blockBody">Get started on your next course or module to continue learning.</Typography>
      <Button
        onClick={
          (e) => {
            triggerLinkClick({ element: e.target });
            document.getElementById('relatedLearning')?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
          }
        }
        className="cta"
        startIcon={<SvgIcon fontSize="medium"><SvgCtaArrowRight /></SvgIcon>}
        variant="text"
      >
        Go to related learning
      </Button>
    </Grid>,
    <Grid item sm={12} md={6} lg={6} key="catalog">
      <Typography variant="headline-small">Browse the catalog</Typography>
      <Typography variant="body-copy-medium" className="blockBody">Find content to continue your learning journey.</Typography>
      <Button onClick={(e) => { triggerLinkClick({ element: e.target }); }} to="/catalog" component={Link} className="cta" startIcon={<SvgIcon fontSize="medium"><SvgCtaArrowRight /></SvgIcon>} variant="text">Go to the catalog</Button>
    </Grid>,
  ];
  if (data.objectType === OBJECT_TYPE_COURSE && (data.meta.type === COURSE_TYPE_SKILL_BUILDER || data.meta.type === COURSE_TYPE_OUTCOMES_CAPABILITIES)) {
    nextStepBlocks.unshift(
      <Grid item sm={12} md={6} lg={6} key="cert">
        <Typography variant="headline-small">Download your certificate of completion</Typography>
        <Typography variant="body-copy-medium" className="blockBody">Your certificate of completion is available to download on My Dashboard.</Typography>
        <Button onClick={(e) => { triggerLinkClick({ element: e.target }); }} to="/dashboard" component={Link} className="cta" startIcon={<SvgIcon fontSize="medium"><SvgCtaArrowRight /></SvgIcon>} variant="text">Go to your dashboard</Button>
      </Grid>,
    );
  }
  if (data?.meta?.completionMessage?.content) {
    const parsedHTML = parseHTMLString({ html: data.meta.completionMessage.content, userId });
    const filteredHTML = Array.isArray(parsedHTML) ? parsedHTML.filter((elem) => elem?.props?.id !== 'removeMeParseHTMLString') : [parsedHTML].filter((elem) => elem?.props?.id !== 'removeMeParseHTMLString');
    if (filteredHTML.length) {
      nextStepBlocks.unshift(<Grid item sm={12} md={6} lg={6} key="custom">{filteredHTML}</Grid>);
    }
  }
  return (
    <RootElement>
      <div className=" rightPadding leftPadding maxWidth">
        <div className="nsBanner">
          <SvgIcon size="large"><SvgComplete /></SvgIcon>
          <span>
            Congratulations,
            {' '}
            {first}
            ! You completed this
            {' '}
            {dataType.toLowerCase()}
            {' '}
            on
            {' '}
            {completionDate}
            .
          </span>
        </div>
      </div>
      <div className="rightPadding leftPadding maxWidth">
        <Typography variant="headline-medium">Next steps</Typography>
        <Grid container className="stepContainer">
          {nextStepBlocks}
        </Grid>
        <Divider flexItem sx={{ marginTop: '48px', marginBottom: '0px' }} />
      </div>
    </RootElement>
  );
};
NextSteps.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    objectType: PropTypes.string,
    meta: PropTypes.shape({
      type: PropTypes.string,
      completionMessage: PropTypes.shape({
        content: PropTypes.string,
      }),
    }),
  }).isRequired,
  learningRecords: PropTypes.shape({}).isRequired,
};

const RootElement = styled.div`
.MuiGrid-root.MuiGrid-item{
  padding-left: 0;
}
  padding-bottom: 24px;
  .cta {
    margin-left: -18px;
  }
  .stepContainer {
    margin-top: 16px;
    p {
      margin-bottom: 4px;
      &:first-child {
        margin-bottom: 12px;
      }
    }
  }
  .nsBanner {
    background-color: ${styleVariables.fillSuccessSlight};
    margin-top: 54px;
    margin-bottom: 54px;
    padding: 12px;
    display: flex;
      
      span:last-child {
        flex: 1;
        margin-left: 16px;
      }
    
  }
  @media screen and (max-width: 600px) {
    .stepContainer > * {
      margin-bottom: 20px;
    }
  }
`;

export default NextSteps;
